import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import { Badge, Factory, Person } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchWorkCenters, loginOperator } from '../../../store/slices/operatorSlice';

const OperatorLogin: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, workCenters } = useSelector((state: RootState) => state.operator);

  const [employeeId, setEmployeeId] = useState('');
  const [workCenterId, setWorkCenterId] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    dispatch(fetchWorkCenters());
  }, [dispatch]);

  const handleLogin = async () => {
    if (!employeeId.trim()) {
      setValidationError('Please enter your Employee ID or Badge Number');
      return;
    }
    const workCenter = workCenters.find((wc) => wc.id === workCenterId);
    if (!workCenter) {
      setValidationError('Please select your work center / station');
      return;
    }
    setValidationError('');
    dispatch(loginOperator({ badgeCode: employeeId.trim(), workCenter }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
    >
      <Card sx={{ maxWidth: 480, width: '100%', mx: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
            <Person sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Operator Workstation
            </Typography>
            <Typography variant="body1" color="textSecondary" textAlign="center">
              Enter your badge number and select your station to begin
            </Typography>
          </Box>

          {(error || validationError) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {validationError || error}
            </Alert>
          )}

          <TextField
            label="Employee ID / Badge Number"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            onKeyDown={handleKeyDown}
            fullWidth
            autoFocus
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Badge />
                </InputAdornment>
              ),
            }}
            placeholder="e.g. EMP-1234"
            sx={{ mb: 2 }}
          />

          <TextField
            label="Work Center / Station"
            value={workCenterId}
            onChange={(e) => setWorkCenterId(e.target.value)}
            select
            fullWidth
            size="medium"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Factory />
                </InputAdornment>
              ),
            }}
            helperText={
              workCenters.length === 0
                ? 'Loading work centers… (master data service must be running)'
                : `${workCenters.length} station(s) available`
            }
            sx={{ mb: 3 }}
          >
            {workCenters.map((wc) => (
              <MenuItem key={wc.id} value={wc.id}>
                {wc.code} — {wc.name}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleLogin}
            disabled={isLoading}
            sx={{ py: 1.5, fontSize: '1rem' }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Log In to Workstation'}
          </Button>

          <Box mt={3} p={2} bgcolor="grey.100" borderRadius={2}>
            <Typography variant="caption" color="textSecondary" display="block" textAlign="center">
              For badge scan issues, contact your supervisor or use the manual ID entry above.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OperatorLogin;
